import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import {
  generatePath, useHistory, withRouter,
} from 'react-router-dom';
import { isFinishedFetching, ResourcesList } from 'redux-and-the-rest';

import { getOrFetchIslands } from '../../redux/reducers/islandReducer';
import { ApplicationState } from '../../redux/types';
import { ModalRoutes, Routes } from '../../routes';
import { IslandModel } from '../../models/islandModel';
import LoadingIndicator from '../shared/LoadingIndicator';
import Env from '../../utils/env';
import useModalLink from '../../hooks/useModalLink';

const { TEACHER_PORTAL } = Env;

interface Props {
  islands: ResourcesList<IslandModel>;
}

/**
 * The index container resolves the path to any existing islands or redirects to the island creation
 * page if none are linked to the user's account.
 */
const IslandContainerIndex = ({ islands }: Props): React.ReactElement => {
  const history = useHistory();
  const openModal = useModalLink();

  useEffect(() => {
    if (isFinishedFetching(islands)) {
      const { items: fetchedIslandItems } = islands;

      if (fetchedIslandItems.length === 0) {
        if (TEACHER_PORTAL) {
          /**
           * We don't auto create new islands for the teacher portal (student's
           * islands are instead explicitly created by their teachers)
           */
          history.replace(generatePath(Routes.NoIslands));
        } else {
          /**
           * We create a new island if the user/guest user does not yet have one.
           */
          history.replace(generatePath(Routes.Islands.New));
        }
      } else {
        /**
         * Otherwise we default to the first island the user has.
         */
        const { values: { id } } = fetchedIslandItems[0];
        if (TEACHER_PORTAL) {
          /**
           * We don't auto redirect users in the teacher portal. Students logging in
           * must explicitly select their island, created for them in advance by their
           * teacher.
           */
          openModal(ModalRoutes.Island.ManageIslands);
        } else {
          history.replace(generatePath(Routes.Islands.View, { id }));
        }
      }
    }
  }, [islands]);

  return <LoadingIndicator />;
};

const mapStateToProps = ({ islands }: ApplicationState) => ({
  islands: getOrFetchIslands(islands),
});

export default withRouter(connect(mapStateToProps)(IslandContainerIndex));
